import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAuthStore } from "@/features/auth/store/auth-store";
import { RESOLUTION_TYPE_OPTIONS, type ResolutionType } from "@/features/cases/types";
import { openCaseFromNotice } from "@/features/notices/api";
import type { Notice } from "@/features/notices/types";
import { listPractitioners, type PractitionerOption } from "@/features/practitioners/api";

interface OpenCaseDialogProps {
  notice: Notice;
  /** Pre-fill the resolution type (e.g. from the advisor recommendation). */
  defaultResolutionType?: ResolutionType;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called with the new case id after it's created. */
  onOpened: (caseId: string) => void;
}

/**
 * Open a resolution Case from a notice: pick a resolution type + practitioner,
 * and choose which of the notice's steps to seed the case with. When no steps
 * are selected, the backend falls back to the firm's default case steps.
 */
export function OpenCaseDialog({
  notice,
  defaultResolutionType,
  open,
  onOpenChange,
  onOpened,
}: OpenCaseDialogProps) {
  const defaultPractitioner = useAuthStore((s) => s.user?.default_practitioner ?? "");
  const [practitioners, setPractitioners] = useState<PractitionerOption[]>([]);
  const [resolutionType, setResolutionType] = useState<string>(
    defaultResolutionType ?? "OTHER",
  );
  const [practitioner, setPractitioner] = useState<string>(defaultPractitioner);
  const [selectedSteps, setSelectedSteps] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);

  // Reset once when the dialog opens (snapshotting the notice's steps at that
  // moment). Keying only on `open` avoids wiping the user's in-progress
  // selections if the parent refetches the notice while the dialog is open.
  useEffect(() => {
    if (!open) return;
    setResolutionType(defaultResolutionType ?? "OTHER");
    setPractitioner(defaultPractitioner);
    // Default to seeding all of the notice's steps into the new case.
    setSelectedSteps(new Set(notice.steps.map((s) => s.id)));
    let active = true;
    listPractitioners()
      .then((list) => active && setPractitioners(list))
      .catch(() => active && setPractitioners([]));
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  function toggleStep(id: string) {
    setSelectedSteps((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function submit() {
    setSaving(true);
    try {
      const { id } = await openCaseFromNotice(notice.id, {
        resolution_type: resolutionType,
        assigned_practitioner: practitioner || null,
        step_ids: [...selectedSteps],
      });
      toast.success("Case opened");
      onOpenChange(false);
      onOpened(id);
    } catch {
      toast.error("Couldn't open a case from this notice.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Open a case from this notice</DialogTitle>
          <DialogDescription>
            Creates a linked case seeded with the selected steps. With none
            selected, your firm's default steps for the resolution type are used.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label>Resolution type</Label>
              <Select value={resolutionType} onValueChange={setResolutionType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {RESOLUTION_TYPE_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label>Practitioner</Label>
              <Select value={practitioner} onValueChange={setPractitioner}>
                <SelectTrigger>
                  <SelectValue placeholder="Unassigned" />
                </SelectTrigger>
                <SelectContent>
                  {practitioners.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {notice.steps.length > 0 && (
            <div className="grid gap-2">
              <Label>Steps to seed</Label>
              <ul className="border-border flex max-h-48 flex-col gap-1 overflow-y-auto rounded-md border p-2">
                {notice.steps.map((step) => (
                  <li key={step.id} className="flex items-center gap-2 px-1 py-1">
                    <Checkbox
                      checked={selectedSteps.has(step.id)}
                      aria-label={`Seed "${step.description}"`}
                      onCheckedChange={() => toggleStep(step.id)}
                    />
                    <span className="text-foreground text-sm">{step.description}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={saving}>
            Open Case
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
